import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import i18n from '@/lib/i18n/i18n';

export interface SelectOption {
    value: string;
    icon?: React.ReactNode;
}

interface CustomSelectProps {
    options: SelectOption[];
    value?: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    width?: string;
    height?: string;
}

const CustomSelect = React.forwardRef<HTMLButtonElement, CustomSelectProps>(
    ({ options, value, onValueChange, placeholder = 'Select...', className, width = 'w-full', height }, ref) => {
        const [isOpen, setIsOpen] = React.useState(false);

        const selectedOption = options.find((option) => option.value === value);

        return (
            <DropdownMenu onOpenChange={(open) => setIsOpen(open)} dir={i18n.dir()}>
                <DropdownMenuTrigger asChild>
                    <Button
                        ref={ref}
                        variant="outline"
                        role="combobox"
                        aria-expanded={isOpen}
                        className={cn(
                            'bg-white-blue-50 text-white-blue-900 items-center justify-center mx-auto hover:text-white-blue-900 focus-visible:ring-0 focus-visible:outline-none border border-white-blue-700 rounded-full',
                            width,
                            height,
                            className,
                        )}
                    >
                        {<>{selectedOption?.icon && <span className="">{selectedOption?.icon}</span>}</> || placeholder}
                        {/* {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />} */}
                    </Button>
                </DropdownMenuTrigger>

                {/* Dropdown Menu Content */}
                <DropdownMenuContent
                    align="start"
                    className={cn(
                        'min-w-[70px] mx-auto items-center justify-center flex flex-col text-white-blue-900 hover:text-white-blue-900',
                        'max-h-[calc(50vh-10px)] overflow-y-auto', // Dynamic height calculation',
                        width,
                    )}
                >
                    {options.map((option) => (
                        <DropdownMenuItem
                            key={option.value}
                            onSelect={() => {
                                onValueChange(option.value);
                                setIsOpen(false);
                            }}
                            className={cn(
                                'cursor-pointer font-cairo bg-white-blue-50 text-[#9E939A] hover:focus:text-[#9E939A] p-3 font-normal text-[14px] leading-[26.24px] tracking-normal',
                                value === option.value && 'bg-accent',
                            )}
                        >
                            {option?.icon && <span>{option?.icon}</span>}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    },
);

CustomSelect.displayName = 'CustomSelect';

export { CustomSelect };
